import axios from "axios";
import Decimal from "decimal.js";
import { useContext, useEffect, useRef, useState } from "react";
import useSWR from "swr";
import { AuthContext } from "../../../context/AuthContext";
import { ADMIN_API_URL, isAffiliateEnvironment } from "../../../utils/env";
import { assertAffiliateEnvironment } from "./affiliateEnvironment";
import { validBudgetHistoryMetadata } from "../../../components/Affiliates/DashboardView/budgetHistory";
import {
  AffiliateDashboardData,
  AffiliatePaymentsPage,
} from "../../../components/Affiliates/DashboardView/types";

export const affiliateAddress = (value: string) =>
  /^0x[0-9a-f]{40}$/i.test(value);
const amount = (value: unknown) =>
  typeof value === "string" &&
  /^\d+(\.\d+)?$/.test(value) &&
  new Decimal(value).isFinite();
const date = (value: unknown) =>
  typeof value === "string" && Number.isFinite(Date.parse(value));
const sameOwner = (a: string | undefined, b: string) =>
  a?.toLowerCase() === b.toLowerCase();
const validRates = (rates: AffiliateDashboardData["floors"]) =>
  rates &&
  [
    rates.perp_taker_bps,
    rates.perp_maker_bps,
    rates.option_taker_bps,
    rates.option_maker_bps,
  ].every(amount);

export function validateAffiliateDashboard(
  data: AffiliateDashboardData,
  owner: string
) {
  assertAffiliateEnvironment(data);
  if (
    !sameOwner(data.affiliate?.address, owner) ||
    (data.admin_nickname !== undefined &&
      typeof data.admin_nickname !== "string") ||
    !date(data.server_time) ||
    typeof data.approved !== "boolean" ||
    !validRates(data.standard_rates) ||
    !validRates(data.ceiling_rates) ||
    !validRates(data.floors) ||
    !data.balances ||
    ![
      data.balances.cash_eligible_usd,
      data.balances.cash_held_usd,
      data.balances.available_points,
      data.balances.pending_points,
    ].every(amount) ||
    !Array.isArray(data.codes) ||
    !Array.isArray(data.wallets) ||
    !data.wallets.every(
      (wallet) =>
        wallet.nickname === undefined || typeof wallet.nickname === "string"
    ) ||
    !Array.isArray(data.allowances) ||
    !validBudgetHistoryMetadata(data) ||
    !Array.isArray(data.fills) ||
    !Array.isArray(data.decisions) ||
    (data.fills_next_cursor !== undefined &&
      typeof data.fills_next_cursor !== "string") ||
    !data.fills.every(
      (fill) =>
        fill?.request &&
        typeof fill.request.fill_id === "string" &&
        affiliateAddress(fill.request.wallet) &&
        date(fill.request.at) &&
        [
          fill.fee_paid,
          fill.fee_waived,
          fill.accrual?.cash_usd,
          fill.accrual?.dao_aevo_usd,
          fill.accrual?.earned_points,
        ].every(amount)
    )
  )
    throw new Error(
      "Dashboard did not match the selected affiliate. Return to the affiliate list and try again."
    );
  return data;
}

export function validateAffiliatePayments(
  data: AffiliatePaymentsPage,
  owner: string,
  cursor: string
) {
  assertAffiliateEnvironment(data);
  if (
    !Array.isArray(data.payments) ||
    data.payments.length > 50 ||
    typeof data.next_cursor !== "string" ||
    (cursor !== "" && data.next_cursor === cursor) ||
    !data.payments.every(
      (row) =>
        sameOwner(row.affiliate, owner) &&
        typeof row.payout_id === "string" &&
        typeof row.transfer_id === "string" &&
        amount(row.amount) &&
        ["reserved", "submitted", "paid"].includes(row.status) &&
        date(row.updated_at) &&
        (row.created_at === null || date(row.created_at)) &&
        (row.status !== "paid" || date(row.paid_at))
    )
  ) {
    throw new Error(
      "Payment history did not match the selected affiliate. Try refreshing the history."
    );
  }
  return data;
}

// Access/validation failures need staff intervention; transient transport errors recover automatically.
export const retryAffiliateRead = (error: unknown) =>
  axios.isAxiosError(error) &&
  (!error.response ||
    error.response.status === 429 ||
    error.response.status >= 500);
const readOptions = {
  keepPreviousData: false,
  refreshInterval: 30000,
  refreshWhenHidden: false,
  revalidateOnFocus: true,
  shouldRetryOnError: retryAffiliateRead,
  errorRetryCount: 3,
  errorRetryInterval: 30000,
};

/** Mounted separately per owner, environment and staff session by AffiliateAdminPage. GET only. */
export function useAffiliateDashboard(owner: string) {
  const { tokenId, queryConfig } = useContext(AuthContext);
  const allowed =
    isAffiliateEnvironment() && Boolean(tokenId) && affiliateAddress(owner);
  const params = new URLSearchParams({ affiliate: owner });
  const url = ADMIN_API_URL(`affiliates/dashboard?${params}`);
  const read = async (cursor = "") => {
    const query = new URLSearchParams({ affiliate: owner });
    if (cursor) query.set("fills_cursor", cursor);
    const response = await axios.get<AffiliateDashboardData>(
      ADMIN_API_URL(`affiliates/dashboard?${query}`),
      queryConfig(tokenId)
    );
    return validateAffiliateDashboard(response.data, owner);
  };
  const swr = useSWR<AffiliateDashboardData>(
    allowed ? [url, tokenId] : null,
    () => read(),
    readOptions
  );
  const base = allowed && !swr.error ? swr.data : undefined;
  const stamp = base
    ? JSON.stringify([
        base.fills_next_cursor,
        base.fills.map((f) => [f.request.wallet, f.request.fill_id]),
      ])
    : "";
  const [extra, setExtra] = useState<{
    stamp: string;
    fills: AffiliateDashboardData["fills"];
    cursor: string;
    seen: string[];
  }>();
  const [loading, setLoading] = useState(false);
  const [fillsError, setFillsError] = useState<unknown>();
  const pending = useRef(false);
  const current = useRef(stamp);
  current.current = stamp;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const appended = extra?.stamp === stamp ? extra : undefined;
  const cursor = appended ? appended.cursor : base?.fills_next_cursor || "";
  const loadMore = async () => {
    if (!allowed || !base || !cursor || pending.current) return;
    pending.current = true;
    setLoading(true);
    setFillsError(undefined);
    try {
      const page = await read(cursor);
      if (!mounted.current || current.current !== stamp) return;
      const next = page.fills_next_cursor || "";
      const seen = [...(appended?.seen || []), cursor];
      if (next && seen.includes(next))
        throw new Error(
          "Trade history cursor did not advance. Refresh this dashboard."
        );
      const records = new Map<
        string,
        AffiliateDashboardData["fills"][number]
      >();
      [...base.fills, ...(appended?.fills || []), ...page.fills].forEach(
        (fill) => {
          records.set(
            `${fill.request.wallet.toLowerCase()}:${fill.request.fill_id}`,
            fill
          );
        }
      );
      const baseKeys = new Set(
        base.fills.map(
          (f) => `${f.request.wallet.toLowerCase()}:${f.request.fill_id}`
        )
      );
      setExtra({
        stamp,
        fills: Array.from(records)
          .filter(([key]) => !baseKeys.has(key))
          .map(([, fill]) => fill),
        cursor: next,
        seen,
      });
    } catch (error) {
      if (mounted.current && current.current === stamp) setFillsError(error);
    } finally {
      pending.current = false;
      if (mounted.current) setLoading(false);
    }
  };
  return {
    ...swr,
    allowed,
    data: base
      ? {
          ...base,
          fills: [...base.fills, ...(appended?.fills || [])],
          fills_next_cursor: cursor,
        }
      : undefined,
    fillsLoading: loading,
    fillsError,
    loadMore,
  };
}

export function useAffiliateDashboardPayments(owner: string) {
  const { tokenId, queryConfig } = useContext(AuthContext);
  const [cursors, setCursors] = useState([""]);
  const [index, setIndex] = useState(0);
  const cursor = cursors[index];
  const allowed =
    isAffiliateEnvironment() && Boolean(tokenId) && affiliateAddress(owner);
  const params = new URLSearchParams({ affiliate: owner, limit: "50" });
  if (cursor) params.set("cursor", cursor);
  const url = ADMIN_API_URL(`affiliates/payments?${params}`);
  const swr = useSWR<AffiliatePaymentsPage>(
    allowed ? [url, tokenId] : null,
    async () => {
      const response = await axios.get<AffiliatePaymentsPage>(
        url,
        queryConfig(tokenId)
      );
      const result = validateAffiliatePayments(response.data, owner, cursor);
      if (
        result.next_cursor &&
        cursors.slice(0, index + 1).includes(result.next_cursor)
      ) {
        throw new Error(
          "Payment history cursor did not advance. Refresh the history."
        );
      }
      return result;
    },
    readOptions
  );
  return {
    data: allowed && !swr.error ? swr.data : undefined,
    error: swr.error,
    isValidating: swr.isValidating,
    page: index + 1,
    previous: () => setIndex((value) => Math.max(0, value - 1)),
    next: () => {
      const next = !swr.error && swr.data?.next_cursor;
      if (!next || swr.isValidating) return;
      setCursors([...cursors.slice(0, index + 1), next]);
      setIndex(index + 1);
    },
    refresh: () => {
      setCursors([""]);
      setIndex(0);
      return index === 0 ? swr.mutate() : Promise.resolve();
    },
  };
}
